'use client'

import {
  File,
  Folder,
  Tree,
  TreeViewElement as TreeViewElementType,
} from '@/components/magicui/file-tree'
import { CodeEditor, Tab } from '@/components/shared/code-editor'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { PanelLeftClose, PanelLeftOpen, PlayCircleIcon } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { ComponentProps, useEffect, useMemo, useState } from 'react'
import { useMediaQuery } from 'usehooks-ts'
import { packageJson } from './package.json'
import { reviewEntityTs } from './review.entity'
import { reviewsPluginTs } from './reviews.plugin'
import { server } from './server'
import { vendureConfig } from './vendure-config'
import { worker } from './worker'

type FileElement = {
  isSelectable?: boolean
  content?: string
} & TreeViewElementType

type FolderElement = {
  initialExpanded?: boolean
  children: TreeViewElement[]
} & TreeViewElementType

type TreeViewElement = FileElement | FolderElement

const elements: TreeViewElement[] = [
  {
    id: 'package-json',
    name: 'package.json',
    isSelectable: true,
    content: packageJson,
  },
  {
    id: '1',
    name: 'src',
    isSelectable: true,
    initialExpanded: true,
    children: [
      {
        id: 'server-file',
        name: 'server.ts',
        isSelectable: true,
        content: server,
      },
      {
        id: 'worker-file',
        name: 'worker.ts',
        isSelectable: true,
        content: worker,
      },
      {
        id: 'config-file',
        name: 'vendure-config.ts',
        isSelectable: true,
        content: vendureConfig,
      },
      {
        id: '2',
        isSelectable: true,
        name: 'plugins',
        initialExpanded: true,
        children: [
          {
            id: 'review-plugin',
            name: 'reviews-plugin',
            children: [
              {
                id: 'entities-folder',
                name: 'entities',
                children: [
                  {
                    id: 'review-entity',
                    name: 'review.entity.ts',
                    isSelectable: true,
                    content: reviewEntityTs,
                  },
                ],
              },
              {
                id: 'review-plugin-file',
                name: 'reviews.plugin.ts',
                isSelectable: true,
                content: reviewsPluginTs,
              },
            ],
          },
        ],
      },
    ],
  },
]

export function CommerceFrameworkHeroVisual({ className, ...props }: ComponentProps<'div'>) {
  const [editorTabs, setEditorTabs] = useState<Tab[]>([])
  const treeViewClosedByDefault = useMediaQuery('(max-width: 1024px)')
  const [treeViewOpen, setTreeViewOpen] = useState(!treeViewClosedByDefault)
  const [started, setStarted] = useState(false)

  const initialExpandedItems = useMemo(() => {
    function getInitialExpandedItems(elements: TreeViewElement[]): string[] {
      const expandedItems: string[] = []

      function traverse(element: TreeViewElement) {
        if ('initialExpanded' in element && element.initialExpanded) {
          expandedItems.push(element.id)
        }
        if ('children' in element) {
          element.children?.forEach(traverse)
        }
      }

      elements.forEach(traverse)
      return expandedItems
    }

    return getInitialExpandedItems(elements)
  }, [elements])

  function toggleEditorTab(editorTab: Tab) {
    const tabExists = editorTabs.some((tab) => tab.filename === editorTab.filename)

    if (!tabExists) {
      setEditorTabs([...editorTabs, editorTab])
    }
  }

  useEffect(() => {
    const initalFile = elements
      .at(0)
      ?.children?.find((child) => child.id === 'config-file') as FileElement

    if (initalFile) {
      setEditorTabs([
        {
          filename: initalFile.name,
          content: initalFile.content ?? '',
        },
      ])
    }
  }, [started])

  function renderTreeElements(elements: TreeViewElement[], toggleEditorTab: (tab: Tab) => void) {
    return elements.map((element) => {
      if ('children' in element) {
        return (
          <Folder
            key={element.id}
            element={element.name}
            value={element.id}
          >
            {renderTreeElements(element.children ?? [], toggleEditorTab)}
          </Folder>
        )
      }

      return (
        <File
          key={element.id}
          value={element.id}
          onClick={() => {
            toggleEditorTab({
              filename: element.name,
              content: element.content ?? '',
            })
          }}
        >
          {element.name}
        </File>
      )
    })
  }

  return (
    <div className="relative">
      <AnimatePresence>
        {!started && (
          <motion.div
            initial={{
              opacity: 0,
            }}
            animate={{
              opacity: 1,
            }}
            exit={{
              opacity: 0,
            }}
            className="absolute top-0 right-0 bottom-0 left-0 z-10 flex items-center justify-center backdrop-blur-sm"
          >
            <Button
              variant="brand"
              size="lg"
              onClick={() => setStarted(true)}
            >
              <PlayCircleIcon />
              Explore the framework
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
      <div
        className={cn(
          'bg-background border-border grid w-full grid-cols-[250px_1fr] gap-2.5 rounded-lg border p-2.5 lg:min-h-[580px]',
          className,
        )}
        {...props}
      >
        <motion.div className={'bg-sidebar border-border space-y-2 rounded-md border p-2.5'}>
          <div className="flex items-center justify-between">
            <span className={'text-muted-foreground block font-medium'}>File Explorer</span>
            <Button
              className="flex lg:hidden"
              variant="ghost"
              size="icon"
              onClick={() => setTreeViewOpen(!treeViewOpen)}
            >
              {treeViewOpen ? <PanelLeftClose /> : <PanelLeftOpen />}
            </Button>
          </div>
          <Tree
            className="grow"
            elements={elements}
            initialExpandedItems={initialExpandedItems}
          >
            {renderTreeElements(elements, toggleEditorTab)}
          </Tree>
        </motion.div>
        <CodeEditor
          editorMaxHeight={600}
          tabs={editorTabs}
          closeTab={(tab) => {
            setEditorTabs(editorTabs.filter((t) => t.filename !== tab.filename))
          }}
        />
      </div>
    </div>
  )
}
